import { API, Constants, type AtomicMove } from "@nextgammon/shared";
import { MaintenanceError } from "./errors";
import { authenticatedApi } from "./index";

/**
 * Type-safe function to parse API responses
 * @param response The fetch Response object
 * @returns A promise resolving to the typed response data
 * @throws Error if the response is not ok, with error message from the API if available
 */
export async function parseApiResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let errorMessage: string;
    try {
      const errorData = await response.json();

      // Check for maintenance mode
      if (response.status === 503 && errorData.maintenance) {
        throw new MaintenanceError(errorData.message || "System under maintenance");
      }

      errorMessage = errorData.error || `API error: ${response.status}`;
    } catch (e) {
      // Re-throw if it's already a MaintenanceError
      if (e instanceof MaintenanceError) throw e;
      errorMessage = `API error: ${response.status}`;
    }
    throw new Error(errorMessage);
  }

  return response.json() as Promise<T>;
}

/**
 * Type-safe API request function with retries
 * @param requestFn Function that makes the API request
 * @param options Options for retries
 * @returns A promise resolving to the typed response data
 */
export async function apiRequest<T>(
  requestFn: () => Promise<Response>,
  options: {
    retries?: number;
    retryDelay?: number;
    errorMessage?: string;
  } = {}
): Promise<T> {
  const { retries = 2, retryDelay = 500, errorMessage } = options;

  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await requestFn();
      return await parseApiResponse<T>(response);
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      if (attempt < retries) {
        // Wait before retry
        await new Promise((resolve) => setTimeout(resolve, retryDelay * (attempt + 1)));
      }
    }
  }

  // All retries failed
  throw lastError || new Error(errorMessage || "API request failed after retries");
}

/**
 * Helper to create a full URL with query parameters
 * @param baseUrl The base URL
 * @param params Query parameters as an object
 * @returns The full URL with query parameters
 */
export function createUrl(
  baseUrl: string,
  params?: Record<string, string | number | boolean | undefined>
): string {
  if (!params) return baseUrl;

  const url = new URL(baseUrl, window.location.origin);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      url.searchParams.append(key, String(value));
    }
  });

  return url.toString();
}

/**
 * Type-safe game API functions that handle dynamic paths
 */
export const gameApi = {
  /**
   * Get a game by ID
   */
  getGame: async (token: string, gameId: string): Promise<API.Game.GetGameResponse> => {
    return apiRequest<API.Game.GetGameResponse>(
      () => {
        const authApi = authenticatedApi(token);
        return authApi.v1.game.$post({ json: { gameId } });
      },
      { errorMessage: "Failed to get game" }
    );
  },

  /**
   * Roll dice for a game
   */
  rollDice: async (token: string, gameId: string): Promise<API.Game.RollDiceResponse> => {
    return apiRequest<API.Game.RollDiceResponse>(
      () => {
        const authApi = authenticatedApi(token);
        return authApi.v1.game.roll.$post({ json: { gameId } });
      },
      { errorMessage: "Failed to roll dice" }
    );
  },

  /**
   * Submit a turn for a game
   */
  submitTurn: async (
    token: string,
    gameId: string,
    moves: AtomicMove[]
  ): Promise<API.Game.SubmitTurnResponse> => {
    return apiRequest<API.Game.SubmitTurnResponse>(
      () => {
        const authApi = authenticatedApi(token);
        return authApi.v1.game.turn.$post({ json: { gameId, moves } });
      },
      { errorMessage: "Failed to submit turn" }
    );
  },

  /**
   * Join a game (register player as ready)
   */
  joinGame: async (token: string, gameId: string): Promise<API.Game.JoinGameResponse> => {
    return apiRequest<API.Game.JoinGameResponse>(
      () => {
        const authApi = authenticatedApi(token);
        return authApi.v1.game.join.$post({ json: { gameId } });
      },
      { errorMessage: "Failed to join game" }
    );
  },

  /**
   * Offer to double the stakes
   */
  offerDouble: async (token: string, gameId: string): Promise<{ success: boolean }> => {
    return apiRequest<{ success: boolean }>(
      () => {
        const authApi = authenticatedApi(token);
        return authApi.v1.game.offerDouble.$post({ json: { gameId } });
      },
      { errorMessage: "Failed to offer double" }
    );
  },

  /**
   * Respond to a double offer
   */
  respondToDouble: async (
    token: string,
    gameId: string,
    accept: boolean
  ): Promise<{ success: boolean }> => {
    return apiRequest<{ success: boolean }>(
      () => {
        const authApi = authenticatedApi(token);
        return authApi.v1.game.respondToDouble.$post({ json: { gameId, accept } });
      },
      { errorMessage: "Failed to respond to double" }
    );
  },

  /**
   * Send an emote in a game
   */
  sendEmote: async (
    token: string,
    gameId: string,
    emoteId: Constants.EmoteId
  ): Promise<{ success: boolean }> => {
    return apiRequest<{ success: boolean }>(
      () => {
        const authApi = authenticatedApi(token);
        return authApi.v1.game.emote.$post({ json: { gameId, emoteId } });
      },
      { errorMessage: "Failed to send emote" }
    );
  },

  /**
   * Resign from a game
   */
  resign: async (token: string, gameId: string): Promise<{ success: boolean }> => {
    return apiRequest<{ success: boolean }>(
      () => {
        const authApi = authenticatedApi(token);
        return authApi.v1.game.resign.$post({ json: { gameId } });
      },
      { errorMessage: "Failed to resign from game" }
    );
  }
};
